import React, { useEffect } from 'react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';

import { config } from '@/config';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { RootState } from '@/configuration/setup/store';
import { accessTokenStored } from '@/configuration/tokenHandling/tokenSlice';
import { isReadinessNotifiedSelector, readinessNotified } from './appSlice';
import { assetTreeSelectionChanged } from './assetTreeSlice';

// Events received from web-workshop
export const TOKEN_RENEWED = 'WEB_WORKSHOP/TOKEN_RENEWED';
const FEATURE_TOGGLE_CHANGED = 'WEB_WORKSHOP/FEATURE_TOGGLE_CHANGED';
const ASSET_TREE_SELECTION_CHANGED = 'WEB_WORKSHOP/ASSET_TREE_SELECTION_CHANGED';
// Events sent to parent frontend
export const MFE_OPEN_DIALOG = 'MFE/OPEN_DIALOG';
export const MFE_NAVIGATE_PARENT = 'MFE/NAVIGATE_PARENT';
const MFE_IS_LOADED = 'MFE/BOOTSTRAP/MFE_IS_LOADED';
export const OPEN_FEEDBACK_DIALOG = 'ENGINE_OIL/OPEN_FEEDBACK_DIALOG';

interface MessageEvent<T> {
    type: string;
    payload: T;
}

export const sendMessage = (message: MessageEvent<any>) => {
    const referrer = document.referrer;
    if (referrer) {
        window.parent.postMessage(message, referrer);
    }
};

export const openPreconditionsDialog = (assetId: string, title: string): void => {
    const url = `${config.frontend.FRONTEND_URI}/#${assetId}/preconditions`;
    sendMessage({
        type: MFE_OPEN_DIALOG,
        payload: {
            title,
            url,
        },
    });
};

export const navigateParentToDtc = (spn: number): void => {
    const to = `?tab=VIEW_DETAIL_DTC&message_type=DM4&spn=${spn}`;
    const options = {
        replace: true,
    };
    sendMessage({ type: MFE_NAVIGATE_PARENT, payload: { to, options } });
};

export const navigateParentToAssetDetail = (assetId: string): void => {
    const to = `/maintenance/detailview/${assetId}?tab=VIEW_DETAIL_MAINTENANCE_AND_FILL_LEVELS`;
    sendMessage({ type: MFE_NAVIGATE_PARENT, payload: { to } });
};

export const openFeedbackDialog = (): void => {
    sendMessage({ type: OPEN_FEEDBACK_DIALOG, payload: {} });
};

export const receiveMessage = (event: any, dispatch: ThunkDispatch<Dispatch<any>, RootState, any>) => {
    const { type: actionType, payload } = event.data;
    if (!actionType) {
        // eslint-disable-next-line no-console
        console.log('Received event in engine-oil without type: ', event);
        return;
    }
    switch (actionType) {
        case TOKEN_RENEWED:
            const { token } = payload;
            dispatch(accessTokenStored(token));
            break;
        case ASSET_TREE_SELECTION_CHANGED:
            dispatch(assetTreeSelectionChanged(payload));
            break;
        case FEATURE_TOGGLE_CHANGED:
            const previousValue = window.localStorage.getItem(payload.key) || 'false';
            if (payload.enabled === 'true') {
                window.localStorage.setItem(payload.key, payload.enabled);
            } else {
                window.localStorage.removeItem(payload.key);
            }
            if (previousValue !== payload.enabled) {
                window.location.reload();
            }
            break;
        default:
            // eslint-disable-next-line no-console
            console.log('Received event in engine-oil with type not handled: ', actionType);
            break;
    }
};

// eslint-disable-next-line react/display-name
const MessageHandler = React.memo(() => {
    const dispatch = useAppDispatch();
    const isReadinessNotified = useAppSelector(isReadinessNotifiedSelector);
    useEffect(() => {
        const listenerCallback = (event: any) => receiveMessage(event, dispatch);
        window.addEventListener('message', listenerCallback, false);
        if (!isReadinessNotified) {
            sendMessage({
                type: MFE_IS_LOADED,
                payload: {
                    mfeName: 'engine-oil',
                },
            });
            dispatch(readinessNotified());
        }
        return () => window.removeEventListener('message', listenerCallback, false);
    }, []);

    return null;
});

export default MessageHandler;
